import React from 'react';
import styled from 'styled-components';
import { Grid, Input, Segment } from 'semantic-ui-react';
import moment from 'moment';
import { FieldLabel } from '../../../../../styles';
import {
  maskCpfOrCnpj,
  maskMoney,
  maskPhoneNumber,
  maskZipCode
} from '../../../../../services/masks';
import { turnUFIntoEstate } from '../../../util';
import { YelumSegimobFireInsurancePolicyType } from './types';
import { UserRolesEnum } from '../../../../../enums/user-roles.enum';
import { get as storage } from '../../../../../services/storage';

interface YelumViewInterface {
  fireInsurance: YelumSegimobFireInsurancePolicyType;
  showJson: boolean;
}

const HeaderAndDviver = styled.h3`
  border-bottom: 1px solid #3498db;
  color: #3498db;
  width: 100%;
  margin-top: 20px !important;
  padding-bottom: 5px;
  letter-spacing: 0.4px;
`;

const DetailInput = styled(Input)`
  cursor: default !important;
  pointer-events: none !important;
  opacity: 0.7;
`;

const DetailField = ({ label, value }: { label: string; value: string | number }) => {
  return (
    <>
      <FieldLabel>{label}</FieldLabel>
      <DetailInput fluid value={value} />
    </>
  );
};

export const YelumAssistanceMap = [
  {
    id: '1',
    nome: 'SEM ASSISTÊNCIA 24H'
  },
  {
    id: '2',
    nome: 'ESSENCIAL'
  },
  {
    id: '3',
    nome: 'MAIS'
  },
  {
    id: '4',
    nome: 'SUPER'
  },
  {
    id: '5',
    nome: 'COM ASSISTÊNCIA 24H'
  }
];
const YelumView = (props: YelumViewInterface) => {
  const { fireInsurance, showJson } = props;

  const coverages = fireInsurance.coverages.filter(coverage => coverage.enabled);
  const role = storage('role');
  const isBrokerUser = role === UserRolesEnum.brokerAdmin || role === UserRolesEnum.brokerAnalyst;
  const getPercentageValue = (...values: any[]) => {
    for (const value of values) {
      if (value === null || value === undefined || value === '') continue;
      const numericValue = Number(value);
      if (!Number.isNaN(numericValue)) return numericValue;
    }
    return 0;
  };
  const proLaborePercentage = getPercentageValue(
    (fireInsurance as any).estateComissionPercentage,
    (fireInsurance as any).proLaborePercentageValue,
    (fireInsurance as any).prolabore
  );
  const brokerCommissionPercentage = getPercentageValue(
    (fireInsurance as any).brokerCommission,
    (fireInsurance as any).commission,
    (fireInsurance as any).brokerCommissionPercentageValue
  );

  const isConsultative = Boolean(fireInsurance.isConsultative);
  // Consultivo em análise/recusado: ainda não há valor definido
  const hasNoPrice =
    isConsultative && ['UNDERANALYSIS', 'DENIED'].includes(fireInsurance.status);

  // Calcular Prêmio Líquido (Prêmio Bruto - IOF)
  const liquidPremium = (fireInsurance.grossPremium || 0) - (fireInsurance.iof || 0);

  return (
    <>
      <Grid style={{ padding: 5 }} columns={'equal'}>
        <HeaderAndDviver>Dados da Cotação</HeaderAndDviver>
        <Grid.Row>
          <Grid.Column>
            <DetailField label={'ID'} value={fireInsurance?.id} />
          </Grid.Column>
          <Grid.Column>
            <DetailField
              label={'Criado em'}
              value={moment(fireInsurance?.created_at).format('DD/MM/YYYY HH:mm')}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <DetailField
              label={'Data de Início'}
              value={moment(fireInsurance?.policyPeriodStart).format('DD/MM/YYYY')}
            />
          </Grid.Column>
          <Grid.Column>
            <DetailField
              label={'Data de Término'}
              value={moment(fireInsurance?.policyPeriodEnd).format('DD/MM/YYYY')}
            />
          </Grid.Column>
          <Grid.Column>
            <DetailField
              label={'Quantidade de Parcelas'}
              value={fireInsurance.installments}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <DetailField label={'Seguradora'} value={fireInsurance?.insurer.name} />
          </Grid.Column>
          <Grid.Column>
            <DetailField
              label={'Imobiliária'}
              value={fireInsurance?.fireInsurance.estate.name}
            />
          </Grid.Column>
          <Grid.Column>
            <DetailField
              label={'Criado por'}
              value={fireInsurance?.fireInsurance.createdBy.name}
            />
          </Grid.Column>
        </Grid.Row>

        <HeaderAndDviver>Dados do Seguro</HeaderAndDviver>
        <Grid.Row>
          <Grid.Column>
            <DetailField
              label={'Código Cotação'}
              value={fireInsurance.referenceCode || 'Não informado'}
            />
          </Grid.Column>
          <Grid.Column>
            <DetailField
              label={'Valor do Aluguel'}
              value={`R$ ${maskMoney(
                String(fireInsurance?.fireInsurance.rentValue || 0),
                true,
                2
              )}`}
            />
          </Grid.Column>
        </Grid.Row>

        {isConsultative && (
          <>
            <Grid.Row>
              <Grid.Column>
                <DetailField label={'Tipo de Orçamento'} value={'Consultivo'} />
              </Grid.Column>
              <Grid.Column>
                <DetailField
                  label={'Estimativa Convencional (referência, mensal)'}
                  value={
                    fireInsurance.conventionalEstimateValue
                      ? `R$ ${maskMoney(
                          String(fireInsurance.conventionalEstimateValue),
                          true,
                          2
                        )}`
                      : '-'
                  }
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column>
                <DetailField
                  label={'Atividade Exercida no Imóvel'}
                  value={fireInsurance.fireInsurance.propertyActivity || 'Não informada'}
                />
              </Grid.Column>
            </Grid.Row>
          </>
        )}

        {!hasNoPrice && (
        <>
        <HeaderAndDviver>Valores Financeiros</HeaderAndDviver>
        <Grid.Row>
          <Grid.Column>
            <DetailField
              label={isConsultative ? 'Valor da Parcela' : 'Valor Mensal Yelum'}
              value={`R$ ${maskMoney(String(fireInsurance.value || 0), true, 2)}`}
            />
          </Grid.Column>
          <Grid.Column>
            <DetailField
              label={'Prêmio Líquido Anual'}
              value={`R$ ${maskMoney(String(liquidPremium), true, 2)}`}
            />
          </Grid.Column>
          <Grid.Column>
            <DetailField
              label={'Prêmio Bruto Anual'}
              value={`R$ ${maskMoney(String(fireInsurance.grossPremium || 0), true, 2)}`}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <DetailField
              label={'IOF'}
              value={`R$ ${maskMoney(String(fireInsurance.iof || 0), true, 2)}`}
            />
          </Grid.Column>
          <Grid.Column>
            <DetailField
              label={'Comissão Imobiliária (Pro Labore)'}
              value={`${proLaborePercentage.toFixed(2)}%`}
            />
          </Grid.Column>
          {isBrokerUser && (
            <Grid.Column>
              <DetailField
                label={'Comissão Corretora'}
                value={`${brokerCommissionPercentage.toFixed(2)}%`}
              />
            </Grid.Column>
          )}
          {!isBrokerUser && <Grid.Column />}
        </Grid.Row>

        <HeaderAndDviver>Parcelamento</HeaderAndDviver>
        <Grid.Row>
          <Grid.Column>
            <DetailField
              label={'Parcelas Yelum'}
              value={`${
                isConsultative ? fireInsurance.installments || 12 : 12
              }x de R$ ${maskMoney(String(fireInsurance.value || 0), true, 2)}`}
            />
          </Grid.Column>
          {fireInsurance.clientInstallments && (
            <>
              <Grid.Column>
                <DetailField
                  label={'Parcelas Cliente'}
                  value={`${fireInsurance.clientInstallments}x`}
                />
              </Grid.Column>
              <Grid.Column>
                <DetailField
                  label={'Valor Parcela Cliente'}
                  value={`R$ ${maskMoney(String(fireInsurance.clientInstallmentValue || 0), true, 2)}`}
                />
              </Grid.Column>
            </>
          )}
          {!fireInsurance.clientInstallments && (
            <>
              <Grid.Column />
              <Grid.Column />
            </>
          )}
        </Grid.Row>

        </>
        )}

        <HeaderAndDviver>Coberturas</HeaderAndDviver>
        {coverages.map((coverage: any) => (
          <Grid.Row key={coverage.id}>
            <Grid.Column>
              <DetailField label={'Cobertura'} value={coverage.coverageName} />
            </Grid.Column>
            <Grid.Column>
              <DetailField
                label={'Valor'}
                value={`R$ ${maskMoney(String(coverage.value || 0), true, 2)}`}
              />
            </Grid.Column>
          </Grid.Row>
        ))}

        <HeaderAndDviver>Dados do Imóvel</HeaderAndDviver>
        <Grid.Row>
          <Grid.Column>
            <DetailField
              label={'CEP'}
              value={maskZipCode(fireInsurance?.fireInsurance.propertyAddresses.zipcode)}
            />
          </Grid.Column>
          <Grid.Column width={6}>
            <DetailField
              label={'Logradouro'}
              value={fireInsurance?.fireInsurance.propertyAddresses.street}
            />
          </Grid.Column>
          <Grid.Column>
            <DetailField
              label={'Número'}
              value={fireInsurance?.fireInsurance.propertyAddresses.number}
            />
          </Grid.Column>
          <Grid.Column width={4}>
            <DetailField
              label={'Complemento'}
              value={
                !!fireInsurance?.fireInsurance.propertyAddresses.complement
                  ? fireInsurance?.fireInsurance.propertyAddresses.complement
                  : 'SEM COMPLEMENTO'
              }
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <DetailField
              label={'Bairro'}
              value={fireInsurance?.fireInsurance.propertyAddresses.district}
            />
          </Grid.Column>
          <Grid.Column>
            <DetailField
              label={'Cidade'}
              value={fireInsurance?.fireInsurance.propertyAddresses.city}
            />
          </Grid.Column>
          <Grid.Column>
            <DetailField
              label={'Estado'}
              value={turnUFIntoEstate(
                fireInsurance?.fireInsurance.propertyAddresses.state
              )}
            />
          </Grid.Column>
          <Grid.Column>
            <DetailField
              label={'Código de Referência'}
              value={fireInsurance?.fireInsurance.propertyExternalCode || 'Não informado'}
            />
          </Grid.Column>
        </Grid.Row>

        <HeaderAndDviver>Dados do Inquilino</HeaderAndDviver>
        <Grid.Row>
          <Grid.Column>
            <DetailField
              label={'Nome/Razão Social'}
              value={fireInsurance?.fireInsurance.tenantName}
            />
          </Grid.Column>
          <Grid.Column>
            <DetailField
              label={'CPF/CNPJ'}
              value={maskCpfOrCnpj(fireInsurance?.fireInsurance.tenantDocument)}
            />
          </Grid.Column>
          <Grid.Column>
            <DetailField
              label={'Telefone'}
              value={maskPhoneNumber(
                fireInsurance?.fireInsurance.tenantCellphone || 'Não informado'
              )}
            />
          </Grid.Column>
        </Grid.Row>

        <HeaderAndDviver>Dados do Proprietário</HeaderAndDviver>
        <Grid.Row>
          <Grid.Column>
            <DetailField
              label={'Nome/Razão Social'}
              value={fireInsurance?.fireInsurance.propertyOwnerName || 'Não informado'}
            />
          </Grid.Column>
          <Grid.Column>
            <DetailField
              label={'CPF/CNPJ'}
              value={maskCpfOrCnpj(
                fireInsurance?.fireInsurance.propertyOwnerDocument || 'Não informado'
              )}
            />
          </Grid.Column>
          <Grid.Column>
            <DetailField
              label={'Telefone'}
              value={maskPhoneNumber(
                fireInsurance?.fireInsurance.propertyOwnerCellphone || 'Não informado'
              )}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <Segment style={{ display: showJson ? 'block' : 'none' }}>
        <pre style={{ width: '100%', height: '40vh', overflowX: 'auto' }}>
          {JSON.stringify(fireInsurance, null, 2)}
        </pre>
      </Segment>
    </>
  );
};

export default YelumView;
